import { useState, useMemo, useEffect, useRef } from 'react';
import { UserCheck, Plus, Copy, Trash2, FileText, Download, Search, Upload, FileSignature, MessageCircle, Pencil, StickyNote, CalendarClock, AlertTriangle, Check, GitMerge, DollarSign, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PLAN_COLOR = { Starter: '#2F80ED', Profesional: '#7B4FD8', 'Travel Empire': '#B8902E' };
const ESTADO_COLOR = { Activo: '#16a34a', 'No Activo': '#dc2626', 'Sin confirmar': '#d97706' };
const PLANES = ['Starter', 'Profesional', 'Travel Empire'];

const copy = (text, msg) => navigator.clipboard.writeText(text).then(() => toast.success(msg));

const parseMonto = (txt) => {
  const n = parseFloat(String(txt || '').replace(/[^0-9.]/g, ''));
  return isNaN(n) ? 0 : n;
};
const fmtMoney = (txt) => {
  const s = String(txt || '').trim();
  if (!s) return '';
  const n = parseMonto(s);
  return n > 0 ? '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : s;
};
const norm = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
const fmtUSD = (n) => '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' USD';
const fmtFecha = (iso) => {
  if (!iso) return '';
  const d = new Date(iso.length <= 10 ? iso + 'T00:00:00' : iso);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString('es', { day: '2-digit', month: 'short', year: 'numeric' });
};
const fmtFechaHora = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString('es', { day: '2-digit', month: 'short', year: 'numeric' }) + ' ' + d.toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' });
};
const diasHasta = (fechaStr) => {
  if (!fechaStr) return null;
  const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
  const f = new Date((fechaStr.length <= 10 ? fechaStr + 'T00:00:00' : fechaStr));
  if (isNaN(f)) return null;
  f.setHours(0, 0, 0, 0);
  return Math.round((f - hoy) / 86400000);
};
// Estado de cobro de un agente según sus cuotas pendientes (ventana de 3 días).
const cobroEstado = (ag) => {
  const cuotas = (ag.planes_pago || []).filter(c => !c.pagado);
  if (!cuotas.length) return null;
  let min = null;
  for (const c of cuotas) {
    const d = diasHasta(c.fecha);
    if (d === null) continue;
    if (min === null || d < min) min = d;
  }
  if (min === null) return null;
  if (min < 0) return { tipo: 'vencida', dias: min };
  if (min <= 3) return { tipo: 'proxima', dias: min };
  return { tipo: 'futura', dias: min };
};
// Código/generación de ingreso = prefijo antes del "-" en numero_socio (ej. "24-003" -> 24).
const genOf = (ag) => {
  const num = ag.numero_socio || '';
  const pref = String(num).split('-')[0];
  const n = parseInt(pref, 10);
  return isNaN(n) ? null : n;
};

const ContratoInfo = ({ ag }) => {
  const c = ag.contrato;
  if (!c) return null;
  return (
    <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-600" data-testid={`agente-contrato-${ag.codigo_ref}`}>
      <div className="flex flex-wrap items-center gap-2 text-xs mb-2">
        <span className="font-mono font-bold text-[#0B1F44] dark:text-white">{c.folio}</span>
        <span className="px-2 py-0.5 rounded-full text-white text-[10px] font-bold uppercase tracking-wide" style={{ background: PLAN_COLOR[c.plan] || '#6b7688' }}>{c.plan}</span>
      </div>
      <div className="flex flex-wrap gap-2">
        <a href={`/contrato.html?id=${ag.contrato_id}`} target="_blank" rel="noopener noreferrer" data-testid={`agente-ver-contrato-${ag.codigo_ref}`}
          className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-md bg-[#0B1F44] text-white hover:bg-[#13306b]">
          <FileText className="h-3 w-3" /> Ver contrato
        </a>
        {c.has_pdf && (
          <a href={`${API_URL}/api/contratos/${ag.contrato_id}/pdf`} data-testid={`agente-pdf-${ag.codigo_ref}`}
            className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-md bg-[#C9A84C] text-[#0B1F44] hover:bg-[#E3C766]">
            <Download className="h-3 w-3" /> Descargar PDF
          </a>
        )}
      </div>
    </div>
  );
};

const PLAN_URL_KEY = { Starter: 'starter', Profesional: 'profesional', 'Travel Empire': 'empire' };

const contratoUrl = (ag) => {
  const p = new URLSearchParams();
  if (ag.nombre) p.set('nombre', ag.nombre);
  if (ag.email) p.set('email', ag.email);
  if (ag.telefono) p.set('telefono', ag.telefono);
  if (ag.pais) p.set('pais', ag.pais);
  if (ag.plan && PLAN_URL_KEY[ag.plan]) p.set('plan', PLAN_URL_KEY[ag.plan]);
  return `/contrato.html?${p.toString()}`;
};

const waUrl = (ag) => {
  const tel = (ag.telefono || '').replace(/[^0-9]/g, '');
  const msg = encodeURIComponent(`Hola ${ag.nombre}, te comparto tu contrato de agente de Club Viajemos.`);
  return `https://wa.me/${tel}?text=${msg}`;
};

const waCobroUrl = (ag) => {
  const tel = (ag.telefono || '').replace(/[^0-9]/g, '');
  const nombre = (ag.nombre || '').split(' ')[0] || ag.nombre || '';
  const msg = encodeURIComponent(
    `Hola ${nombre}, te saludamos de Club Viajemos 🌎. Te recordamos que mantienes un saldo pendiente de ${ag.deuda_pendiente} de tu plan${ag.plan ? ` (${ag.plan})` : ''}. Este saldo se va descontando con cada comisión, sin plazo obligatorio y sin intereses. Si deseas adelantar un pago, con gusto te ayudamos. ¡Gracias!`
  );
  return `https://wa.me/${tel}?text=${msg}`;
};

const waFirmaUrl = (ag) => {
  const tel = (ag.telefono || '').replace(/[^0-9]/g, '');
  const nombre = (ag.nombre || '').split(' ')[0] || ag.nombre || '';
  const link = (typeof window !== 'undefined' ? window.location.origin : '') + contratoUrl(ag);
  const msg = encodeURIComponent(
    `Hola ${nombre} 👋, te saludamos de Club Viajemos 🌎. Vemos que tu contrato de agente aún está pendiente de firma. Para completarlo solo entra a este enlace y fírmalo en línea:\n\n${link}\n\nSolo toma un par de minutos. Cualquier duda, con gusto te ayudamos. ¡Bienvenido al equipo! ✍️`
  );
  return `https://wa.me/${tel}?text=${msg}`;
};

const inputCls = 'px-2 py-1 text-xs rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 w-full';

const PatrocinadorSelect = ({ ag, allAgentes = [], onSetPatrocinador }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);
  const current = ag.patrocinador_nombre || 'Club Viajemos';

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const results = useMemo(() => {
    if (!open) return [];
    const nq = norm(q.trim());
    const list = allAgentes.filter(a => a.id !== ag.id);
    const filtered = nq ? list.filter(a => norm(`${a.nombre} ${a.codigo_ref}`).includes(nq)) : list;
    return filtered.slice(0, 50);
  }, [open, q, allAgentes, ag.id]);

  const pick = (id, nombre) => { onSetPatrocinador && onSetPatrocinador(ag.id, id, nombre); setOpen(false); setQ(''); };

  return (
    <div className="relative inline-block" ref={ref}>
      <button type="button" onClick={() => setOpen(o => !o)}
        className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 max-w-[220px] truncate"
        data-testid={`agente-patrocinador-${ag.codigo_ref}`}>
        <span className="truncate">{current}</span>
        <ChevronDown className="h-3 w-3 shrink-0 text-slate-400" />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-64 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-md shadow-lg p-2">
          <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar agencia…"
            className="w-full px-2 py-1 text-xs rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700"
            data-testid={`agente-patrocinador-search-${ag.codigo_ref}`} />
          <ul className="max-h-60 overflow-auto mt-1">
            <li>
              <button type="button" onClick={() => pick('', 'Club Viajemos')}
                className="w-full text-left px-2 py-1.5 text-xs rounded hover:bg-slate-100 dark:hover:bg-slate-700 font-semibold text-[#45A0EE]"
                data-testid={`agente-patrocinador-opt-default-${ag.codigo_ref}`}>
                Club Viajemos (por defecto)
              </button>
            </li>
            {results.map(a => (
              <li key={a.id}>
                <button type="button" onClick={() => pick(a.id, a.nombre)}
                  className="w-full text-left px-2 py-1.5 text-xs rounded hover:bg-slate-100 dark:hover:bg-slate-700 truncate">
                  {a.nombre}{a.codigo_ref ? <span className="text-slate-400"> ({a.codigo_ref})</span> : null}
                </button>
              </li>
            ))}
            {q && results.length === 0 && (
              <li className="text-xs text-slate-400 px-2 py-1.5">Sin resultados</li>
            )}
            {!q && allAgentes.length > 50 && (
              <li className="text-[10px] text-slate-400 px-2 py-1">Escribe para buscar entre {allAgentes.length} agencias…</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

const AgenteCard = ({ ag, onPatch, onDelete, onRefresh, allAgentes = [], onSetPatrocinador }) => {
  const [editing, setEditing] = useState(false);
  const [showObs, setShowObs] = useState(false);
  const [showPagos, setShowPagos] = useState(false);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({ nombre: ag.nombre || '', email: ag.email || '', telefono: ag.telefono || '', pais: ag.pais || '', plan: ag.plan || '', deuda_pendiente: ag.deuda_pendiente || '', monto_pagado: ag.monto_pagado || '' });
  const [obs, setObs] = useState('');
  const [cuota, setCuota] = useState({ monto: '', fecha: '', descripcion: '' });

  const refresh = onRefresh || (() => {});

  const saveEdit = async () => {
    if (!f.nombre.trim()) { toast.error('El nombre no puede estar vacío'); return; }
    setSaving(true);
    const ok = await onPatch(ag.id, { nombre: f.nombre, email: f.email, telefono: f.telefono, pais: f.pais, plan: f.plan, deuda_pendiente: f.deuda_pendiente, monto_pagado: f.monto_pagado });
    setSaving(false);
    if (ok) setEditing(false);
  };

  const addObs = async () => {
    if (!obs.trim()) { toast.error('Escribe la observación'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/agentes/${ag.id}/observacion`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ texto: obs.trim() }),
      });
      if (res.ok) { toast.success('Observación agregada'); setObs(''); refresh(); }
      else toast.error('No se pudo agregar');
    } catch { toast.error('Error de conexión'); }
    setSaving(false);
  };
  const delObs = async (obsId) => {
    try {
      await fetch(`${API_URL}/api/admin/agentes/${ag.id}/observacion/${obsId}`, { method: 'DELETE' });
      refresh();
    } catch { toast.error('Error de conexión'); }
  };

  const addCuota = async () => {
    if (!cuota.monto.trim() || !cuota.fecha.trim()) { toast.error('Monto y fecha son obligatorios'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/agentes/${ag.id}/plan-pago`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(cuota),
      });
      if (res.ok) { toast.success('Cuota agregada'); setCuota({ monto: '', fecha: '', descripcion: '' }); refresh(); }
      else toast.error('No se pudo agregar la cuota');
    } catch { toast.error('Error de conexión'); }
    setSaving(false);
  };
  const toggleCuota = async (cuotaId, pagado) => {
    try {
      await fetch(`${API_URL}/api/admin/agentes/${ag.id}/plan-pago/${cuotaId}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ pagado }),
      });
      refresh();
    } catch { toast.error('Error de conexión'); }
  };
  const delCuota = async (cuotaId) => {
    try {
      await fetch(`${API_URL}/api/admin/agentes/${ag.id}/plan-pago/${cuotaId}`, { method: 'DELETE' });
      refresh();
    } catch { toast.error('Error de conexión'); }
  };

  const estado = ag.estado || 'Activo';
  const obsLog = Array.isArray(ag.observaciones_log) ? [...ag.observaciones_log].reverse() : [];
  const planesPago = Array.isArray(ag.planes_pago) ? [...ag.planes_pago].sort((a, b) => (a.fecha || '').localeCompare(b.fecha || '')) : [];
  const cuotasPend = planesPago.filter(c => !c.pagado).length;
  const cobro = cobroEstado(ag);

  return (
    <div className="p-4 bg-white dark:bg-slate-700/50 rounded-lg border border-slate-200 dark:border-slate-600" data-testid={`agente-card-${ag.codigo_ref}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span className="font-semibold text-sm">{ag.nombre}</span>
            <span className="px-2 py-0.5 bg-[#D4AF37]/15 text-[#D4AF37] text-[10px] font-mono rounded">{ag.codigo_ref}</span>
            {ag.numero_socio && <span className="px-2 py-0.5 bg-slate-200 dark:bg-slate-600 text-slate-600 dark:text-slate-200 text-[10px] font-mono rounded">Socio {ag.numero_socio}</span>}
            <span className="px-2 py-0.5 rounded-full text-white text-[10px] font-bold uppercase tracking-wide" style={{ background: ESTADO_COLOR[estado] || '#64748b' }} data-testid={`agente-estado-badge-${ag.codigo_ref}`}>{estado}</span>
            {ag.plan && (
              <span className="px-2 py-0.5 rounded-full text-white text-[10px] font-bold uppercase tracking-wide" style={{ background: PLAN_COLOR[ag.plan] || '#6b7688' }}>{ag.plan}</span>
            )}
            {cobro && cobro.tipo === 'vencida' && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#b42318] text-white text-[10px] font-bold uppercase tracking-wide" data-testid={`agente-cobro-badge-${ag.codigo_ref}`}>
                <AlertTriangle className="h-3 w-3" /> Cobro vencido
              </span>
            )}
            {cobro && cobro.tipo === 'proxima' && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500 text-white text-[10px] font-bold uppercase tracking-wide" data-testid={`agente-cobro-badge-${ag.codigo_ref}`}>
                <CalendarClock className="h-3 w-3" /> {cobro.dias === 0 ? 'Cobro hoy' : `Cobro en ${cobro.dias}d`}
              </span>
            )}
          </div>
          {(ag.email || ag.telefono || ag.pais) && (
            <div className="text-xs text-slate-500 truncate">{[ag.email, ag.telefono, ag.pais].filter(Boolean).join(' · ')}</div>
          )}
          <div className="text-xs text-slate-500 mt-0.5" data-testid={`agente-patrocinador-label-${ag.codigo_ref}`}>
            Patrocinada por: <span className="font-semibold text-[#45A0EE]">{ag.patrocinador_nombre || 'Club Viajemos'}</span>
          </div>
          {Array.isArray(ag.prospectos) && ag.prospectos.length > 0 && (
            <div className="mt-2 rounded-md bg-amber-50 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-900/20 px-3 py-2" data-testid={`agente-prospectos-${ag.codigo_ref}`}>
              <div className="text-[10px] font-bold uppercase tracking-wide text-amber-700 dark:text-amber-400 mb-1">Clientes a registrar ({ag.prospectos.length})</div>
              <ul className="space-y-0.5">
                {ag.prospectos.map((p, i) => (
                  <li key={i} className="text-xs text-slate-600 dark:text-slate-300">
                    {[p.nombre, p.telefono, p.email, p.pais, p.plan].filter(Boolean).join(' · ') || 'Sin datos'}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-3 mt-2">
            <div className="flex items-center gap-1">
              <label className="text-[11px] font-semibold text-slate-500">Estado:</label>
              <select value={estado} onChange={e => onPatch(ag.id, { estado: e.target.value })}
                className="px-2 py-1 text-xs rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700"
                data-testid={`agente-estado-${ag.codigo_ref}`}>
                <option value="Activo">ACTIVO</option>
                <option value="No Activo">NO ACTIVO</option>
                <option value="Sin confirmar">SIN CONFIRMAR</option>
              </select>
            </div>
            <div className="flex items-center gap-1">
              <label className="text-[11px] font-semibold text-slate-500">Plan:</label>
              <select value={ag.plan || ''} onChange={e => onPatch(ag.id, { plan: e.target.value })}
                className="px-2 py-1 text-xs rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700"
                data-testid={`agente-plan-${ag.codigo_ref}`}>
                <option value="">Sin plan</option>
                {PLANES.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-1">
              <label className="text-[11px] font-semibold text-slate-500">Patrocinado por:</label>
              <PatrocinadorSelect ag={ag} allAgentes={allAgentes} onSetPatrocinador={onSetPatrocinador} />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500 mt-1">
            <span>Este mes: <strong className={ag.certificados_mes >= 500 ? 'text-red-500' : 'text-[#80C753]'}>{ag.certificados_mes}/500</strong></span>
            <span>Total: <strong>{ag.total_certificados}</strong></span>
            <span data-testid={`agente-pagado-${ag.codigo_ref}`}>Pagado: {ag.monto_pagado ? <strong className="text-emerald-600">{fmtMoney(ag.monto_pagado)}</strong> : <strong className="text-slate-400">—</strong>}</span>
            <span data-testid={`agente-deuda-${ag.codigo_ref}`}>Deuda pendiente: {ag.deuda_pendiente ? <strong className="text-red-600">{ag.deuda_pendiente}</strong> : <strong className="text-emerald-600">Sin deuda</strong>}</span>
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <button onClick={() => { setEditing(v => !v); setShowObs(false); }}
              className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-md bg-slate-700 text-white hover:bg-slate-800"
              data-testid={`agente-editar-${ag.codigo_ref}`}>
              <Pencil className="h-3 w-3" /> Editar
            </button>
            <button onClick={() => { setShowObs(v => !v); setEditing(false); setShowPagos(false); }}
              className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-md bg-amber-500 text-white hover:bg-amber-600"
              data-testid={`agente-observaciones-${ag.codigo_ref}`}>
              <StickyNote className="h-3 w-3" /> Observaciones{obsLog.length ? ` (${obsLog.length})` : (ag.observaciones ? ' ✓' : '')}
            </button>
            <button onClick={() => { setShowPagos(v => !v); setEditing(false); setShowObs(false); }}
              className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-md bg-[#6d28d9] text-white hover:bg-[#5b21b6]"
              data-testid={`agente-planes-pago-${ag.codigo_ref}`}>
              <CalendarClock className="h-3 w-3" /> Planes de pago{cuotasPend ? ` (${cuotasPend})` : ''}
            </button>
            <a href={contratoUrl(ag)} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-md bg-[#0B1F44] text-white hover:bg-[#13306b]"
              data-testid={`agente-usar-contrato-${ag.codigo_ref}`}>
              <FileSignature className="h-3 w-3" /> Usar para contrato
            </a>
            {ag.telefono && (
              <a href={waUrl(ag)} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-md bg-[#25D366] text-white hover:bg-[#1eb959]"
                data-testid={`agente-whatsapp-${ag.codigo_ref}`}>
                <MessageCircle className="h-3 w-3" /> WhatsApp
              </a>
            )}
            {ag.telefono && ag.deuda_pendiente && (
              <a href={waCobroUrl(ag)} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-md bg-[#b42318] text-white hover:bg-[#8a1c1c]"
                data-testid={`agente-cobro-${ag.codigo_ref}`}>
                <MessageCircle className="h-3 w-3" /> Cobrar deuda
              </a>
            )}
            {ag.telefono && !(ag.contrato && ag.contrato.has_pdf) && (
              <a href={waFirmaUrl(ag)} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-md bg-[#0e7490] text-white hover:bg-[#155e75]"
                data-testid={`agente-whatsapp-firma-${ag.codigo_ref}`}>
                <FileSignature className="h-3 w-3" /> Recordar firma
              </a>
            )}
            <button
              onClick={() => copy(`${window.location.origin}/codigosdeviaje500dolares?ref=${ag.codigo_ref}`, 'Link de referido copiado')}
              className="text-[10px] text-[#45A0EE] hover:underline flex items-center gap-1"
              data-testid={`agente-link-${ag.codigo_ref}`}>
              <Copy className="h-3 w-3" /> Copiar link de referido
            </button>
          </div>

          {editing && (
            <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-600 grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid={`agente-editar-form-${ag.codigo_ref}`}>
              <input className={inputCls} placeholder="Nombre" value={f.nombre} onChange={e => setF({ ...f, nombre: e.target.value })} data-testid={`agente-edit-nombre-${ag.codigo_ref}`} />
              <input className={inputCls} placeholder="Email" value={f.email} onChange={e => setF({ ...f, email: e.target.value })} data-testid={`agente-edit-email-${ag.codigo_ref}`} />
              <input className={inputCls} placeholder="Teléfono" value={f.telefono} onChange={e => setF({ ...f, telefono: e.target.value })} data-testid={`agente-edit-telefono-${ag.codigo_ref}`} />
              <input className={inputCls} placeholder="País" value={f.pais} onChange={e => setF({ ...f, pais: e.target.value })} data-testid={`agente-edit-pais-${ag.codigo_ref}`} />
              <select className={inputCls} value={f.plan} onChange={e => setF({ ...f, plan: e.target.value })} data-testid={`agente-edit-plan-${ag.codigo_ref}`}>
                <option value="">Sin plan</option>
                {PLANES.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
              <input className={inputCls} placeholder="Deuda pendiente (ej: $200)" value={f.deuda_pendiente} onChange={e => setF({ ...f, deuda_pendiente: e.target.value })} data-testid={`agente-edit-deuda-${ag.codigo_ref}`} />
              <input className={inputCls} placeholder="Monto pagado (ej: $497.00)" value={f.monto_pagado} onChange={e => setF({ ...f, monto_pagado: e.target.value })} data-testid={`agente-edit-pagado-${ag.codigo_ref}`} />
              <div className="flex items-center gap-2">
                <button onClick={saveEdit} disabled={saving} className="text-[11px] font-semibold px-3 py-1 rounded-md bg-[#D4AF37] text-[#1A1A2E] hover:bg-[#B8860B] disabled:opacity-60" data-testid={`agente-edit-guardar-${ag.codigo_ref}`}>{saving ? 'Guardando…' : 'Guardar'}</button>
                <button onClick={() => setEditing(false)} className="text-[11px] px-3 py-1 rounded-md border border-slate-300 dark:border-slate-600">Cancelar</button>
              </div>
            </div>
          )}

          {showObs && (
            <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-600" data-testid={`agente-observaciones-form-${ag.codigo_ref}`}>
              <div className="flex items-start gap-2">
                <textarea className="flex-1 px-3 py-2 text-xs rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 min-h-[60px]"
                  placeholder="Escribe una nota de seguimiento. Se guarda con la fecha de hoy…" value={obs} onChange={e => setObs(e.target.value)}
                  data-testid={`agente-obs-text-${ag.codigo_ref}`} />
                <button onClick={addObs} disabled={saving} className="shrink-0 text-[11px] font-semibold px-3 py-2 rounded-md bg-[#D4AF37] text-[#1A1A2E] hover:bg-[#B8860B] disabled:opacity-60" data-testid={`agente-obs-guardar-${ag.codigo_ref}`}>{saving ? '…' : 'Agregar'}</button>
              </div>
              {obsLog.length > 0 && (
                <ul className="mt-3 space-y-2" data-testid={`agente-obs-log-${ag.codigo_ref}`}>
                  {obsLog.map((o) => (
                    <li key={o.id} className="flex items-start gap-2 text-xs bg-amber-50 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-900/20 rounded-md px-3 py-2">
                      <CalendarClock className="h-3.5 w-3.5 text-amber-600 mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-amber-700 dark:text-amber-400 text-[10px]">{fmtFechaHora(o.fecha)}</div>
                        <div className="text-slate-600 dark:text-slate-300 whitespace-pre-wrap">{o.texto}</div>
                      </div>
                      <button onClick={() => delObs(o.id)} className="text-red-400 hover:text-red-600 shrink-0" data-testid={`agente-obs-del-${o.id}`}><Trash2 className="h-3 w-3" /></button>
                    </li>
                  ))}
                </ul>
              )}
              {ag.observaciones && (
                <div className="mt-2 text-xs text-slate-500 italic">Nota anterior: {ag.observaciones}</div>
              )}
            </div>
          )}

          {showPagos && (
            <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-600" data-testid={`agente-planes-pago-form-${ag.codigo_ref}`}>
              <div className="text-[11px] font-bold uppercase tracking-wide text-[#6d28d9] mb-2 flex items-center gap-1">
                <CalendarClock className="h-3.5 w-3.5" /> Plan de pago acordado · recordatorio {`<= 3 días`}
              </div>
              {planesPago.length > 0 && (
                <ul className="space-y-2 mb-3" data-testid={`agente-cuotas-${ag.codigo_ref}`}>
                  {planesPago.map((c) => {
                    const d = diasHasta(c.fecha);
                    const venc = !c.pagado && d !== null && d < 0;
                    const prox = !c.pagado && d !== null && d >= 0 && d <= 3;
                    return (
                      <li key={c.id} className={`flex items-center gap-2 text-xs rounded-md px-3 py-2 border ${c.pagado ? 'bg-emerald-50 dark:bg-emerald-900/10 border-emerald-200 dark:border-emerald-900/30' : venc ? 'bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-900/30' : prox ? 'bg-amber-50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-900/30' : 'bg-slate-50 dark:bg-slate-700/40 border-slate-200 dark:border-slate-600'}`}>
                        <button onClick={() => toggleCuota(c.id, !c.pagado)} title={c.pagado ? 'Marcar como no pagado' : 'Marcar como pagado'}
                          className={`shrink-0 h-5 w-5 rounded-full border flex items-center justify-center ${c.pagado ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-slate-400'}`}
                          data-testid={`agente-cuota-toggle-${c.id}`}>
                          {c.pagado && <Check className="h-3 w-3" />}
                        </button>
                        <div className="flex-1 min-w-0">
                          <span className="font-bold text-slate-700 dark:text-slate-200">{fmtMoney(c.monto)}</span>
                          <span className="text-slate-400 mx-1.5">·</span>
                          <span className={venc ? 'text-red-600 font-semibold' : prox ? 'text-amber-600 font-semibold' : 'text-slate-500'}>{fmtFecha(c.fecha)}</span>
                          {!c.pagado && venc && <span className="ml-1.5 text-[10px] font-bold text-red-600 uppercase">Vencida</span>}
                          {!c.pagado && prox && <span className="ml-1.5 text-[10px] font-bold text-amber-600 uppercase">{d === 0 ? 'Hoy' : `En ${d}d`}</span>}
                          {c.pagado && <span className="ml-1.5 text-[10px] font-bold text-emerald-600 uppercase">Pagado</span>}
                          {c.descripcion && <div className="text-slate-400 truncate">{c.descripcion}</div>}
                        </div>
                        <button onClick={() => delCuota(c.id)} className="text-red-400 hover:text-red-600 shrink-0" data-testid={`agente-cuota-del-${c.id}`}><Trash2 className="h-3 w-3" /></button>
                      </li>
                    );
                  })}
                </ul>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                <input className={inputCls} placeholder="Monto (ej: $97)" value={cuota.monto} onChange={e => setCuota({ ...cuota, monto: e.target.value })} data-testid={`agente-cuota-monto-${ag.codigo_ref}`} />
                <input className={inputCls} type="date" value={cuota.fecha} onChange={e => setCuota({ ...cuota, fecha: e.target.value })} data-testid={`agente-cuota-fecha-${ag.codigo_ref}`} />
                <input className={inputCls} placeholder="Descripción (opcional)" value={cuota.descripcion} onChange={e => setCuota({ ...cuota, descripcion: e.target.value })} data-testid={`agente-cuota-desc-${ag.codigo_ref}`} />
                <button onClick={addCuota} disabled={saving} className="text-[11px] font-semibold px-3 py-2 rounded-md bg-[#6d28d9] text-white hover:bg-[#5b21b6] disabled:opacity-60 inline-flex items-center justify-center gap-1" data-testid={`agente-cuota-agregar-${ag.codigo_ref}`}>
                  <Plus className="h-3 w-3" /> {saving ? '…' : 'Agregar cuota'}
                </button>
              </div>
            </div>
          )}

          {Array.isArray(ag.patrocinados) && ag.patrocinados.length > 0 && (
            <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-600" data-testid={`agente-patrocinados-${ag.codigo_ref}`}>
              <div className="text-[10px] font-bold uppercase tracking-wide text-[#45A0EE] mb-1 flex items-center gap-1">
                <GitMerge className="h-3.5 w-3.5" /> Agencias ingresadas por él ({ag.patrocinados.length})
              </div>
              <ul className="space-y-0.5">
                {[...ag.patrocinados].sort((a, b) => (b.fecha || '').localeCompare(a.fecha || '')).map((p) => {
                  const live = allAgentes.find(a => a.id === p.id);
                  const nombre = (live && live.nombre) || p.nombre || 'Sin nombre';
                  const plan = (live && live.plan) || p.plan;
                  return (
                    <li key={p.id} className="text-xs text-slate-600 dark:text-slate-300">
                      <span className="font-semibold">{nombre}</span>
                      {p.codigo_ref ? <span className="text-slate-400"> ({p.codigo_ref})</span> : null}
                      {plan ? <span className="text-slate-400"> · {plan}</span> : null}
                      {p.fecha ? <span className="text-slate-400"> · {fmtFecha(p.fecha)}</span> : null}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          <ContratoInfo ag={ag} />
        </div>
        <button onClick={() => onDelete(ag.id)}
          className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors shrink-0"
          data-testid={`agente-delete-${ag.codigo_ref}`}>
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

const AgentesTab = ({ newAgente, setNewAgente, createAgente, agentes, deleteAgente, loadAgentes }) => {
  const [query, setQuery] = useState('');
  const [estadoFilter, setEstadoFilter] = useState('todos');
  const [planFilter, setPlanFilter] = useState('todos');
  const [soloDeuda, setSoloDeuda] = useState(false);
  const [soloContratoPendiente, setSoloContratoPendiente] = useState(false);
  const [pagoMin, setPagoMin] = useState('');
  const [pagoMax, setPagoMax] = useState('');
  const [genFilter, setGenFilter] = useState('todas');
  const [importing, setImporting] = useState(false);
  const [merging, setMerging] = useState(false);
  const [cobros, setCobros] = useState([]);
  const [showCobros, setShowCobros] = useState(false);
  const [form, setForm] = useState({ nombre: '', codigo_ref: '', email: '', telefono: '', pais: '', plan: '' });
  const [creating, setCreating] = useState(false);

  const loadCobros = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/cobros-proximos?dias=3`);
      const data = await res.json();
      if (res.ok) setCobros(data.cobros || []);
    } catch { /* noop */ }
  };
  useEffect(() => { loadCobros(); }, [agentes]);

  const fusionarDuplicados = async () => {
    if (!window.confirm('Esto fusionará TODOS los agentes repetidos (por email, nombre o teléfono) en un solo registro y eliminará los duplicados de forma permanente. ¿Continuar?')) return;
    setMerging(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/agentes/merge-duplicados`, { method: 'POST' });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Fusión lista: ${data.grupos_fusionados} grupos, ${data.agentes_eliminados} duplicados eliminados`);
        loadAgentes && loadAgentes();
      } else toast.error(data.detail || 'No se pudo fusionar');
    } catch { toast.error('Error de conexión al fusionar'); }
    setMerging(false);
  };

  const handleCreate = async () => {
    if (!form.nombre.trim()) {
      toast.error('Escribe el nombre del agente');
      return;
    }
    setCreating(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/agentes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (res.ok) {
        const code = data.agente?.codigo_ref;
        toast.success(`Agente "${data.agente?.nombre}" creado`);
        setForm({ nombre: '', codigo_ref: '', email: '', telefono: '', pais: '', plan: '' });
        setEstadoFilter('todos');
        setPlanFilter('todos');
        setQuery(code || '');
        loadAgentes && loadAgentes();
      } else {
        toast.error(data.detail || 'No se pudo crear el agente');
      }
    } catch {
      toast.error('Error de conexión al crear el agente');
    }
    setCreating(false);
  };

  const patchAgente = async (id, payload) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/agentes/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success('Agente actualizado');
        loadAgentes && loadAgentes();
        return true;
      }
      toast.error(data.detail || 'No se pudo actualizar');
      return false;
    } catch {
      toast.error('Error de conexión al actualizar');
      return false;
    }
  };

  const setPatrocinador = async (id, patrocinador_id, patrocinador_nombre) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/agentes/${id}/patrocinador`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ patrocinador_id: patrocinador_id || null, patrocinador_nombre: patrocinador_nombre || null }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Patrocinador: ${data.patrocinador_nombre}`);
        loadAgentes && loadAgentes();
        return true;
      }
      toast.error(data.detail || 'No se pudo asignar el patrocinador');
      return false;
    } catch {
      toast.error('Error de conexión al asignar patrocinador');
      return false;
    }
  };

  const exportarExcel = () => {
    if (!filtered.length) { toast.error('No hay agentes en el filtro para exportar'); return; }
    const cols = [
      ['Nombre', a => a.nombre],
      ['Código referido', a => a.codigo_ref],
      ['N° Socio', a => a.numero_socio],
      ['Generación', a => { const g = genOf(a); return g === null ? '' : String(g).padStart(2, '0'); }],
      ['Estado', a => a.estado || 'Activo'],
      ['Plan', a => a.plan],
      ['País', a => a.pais],
      ['Email', a => a.email],
      ['Teléfono', a => a.telefono],
      ['Certificados mes', a => a.certificados_mes],
      ['Total certificados', a => a.total_certificados],
      ['Monto pagado', a => a.monto_pagado ? parseMonto(a.monto_pagado).toFixed(2) : ''],
      ['Deuda pendiente', a => a.deuda_pendiente ? parseMonto(a.deuda_pendiente).toFixed(2) : ''],
      ['Folio contrato', a => a.contrato?.folio || ''],
      ['Estado contrato', a => a.contrato ? (a.contrato.has_pdf ? 'Firmado (PDF)' : 'Generado') : 'Sin contrato'],
      ['Observaciones', a => a.observaciones],
    ];
    const esc = (v) => {
      const s = v === undefined || v === null ? '' : String(v);
      return /[",;\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = [cols.map(c => c[0]).join(',')];
    filtered.forEach(a => rows.push(cols.map(c => esc(c[1](a))).join(',')));
    // fila de totales
    rows.push('');
    rows.push([`Total (${filtered.length} agentes)`, '', '', '', '', '', '', '', '', '', '', totalPagado.toFixed(2), totalDeuda.toFixed(2), '', '', ''].map(esc).join(','));
    const csv = '\uFEFF' + rows.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `agentes_clubviajemos_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(`Exportados ${filtered.length} agentes`);
  };

  const importarSocios = async () => {
    setImporting(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/agentes/importar`, { method: 'POST' });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Importación lista: ${data.creados} nuevos, ${data.actualizados} actualizados`);
        loadAgentes && loadAgentes();
      } else {
        toast.error(data.detail || 'Error al importar');
      }
    } catch {
      toast.error('Error de conexión al importar');
    }
    setImporting(false);
  };

  const importarCobranza = async () => {
    setImporting(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/agentes/importar-cobranza`, { method: 'POST' });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Cobranza Mayo 04: ${data.creados} nuevos, ${data.actualizados} actualizados`);
        loadAgentes && loadAgentes();
      } else {
        toast.error(data.detail || 'Error al importar cobranza');
      }
    } catch {
      toast.error('Error de conexión al importar cobranza');
    }
    setImporting(false);
  };

  const importarPagos = async () => {
    setImporting(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/agentes/importar-pagos`, { method: 'POST' });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Pagos cargados: ${data.actualizados} agentes actualizados (${data.sin_agente} socios sin agente)`);
        loadAgentes && loadAgentes();
      } else {
        toast.error(data.detail || 'Error al importar pagos');
      }
    } catch {
      toast.error('Error de conexión al importar pagos');
    }
    setImporting(false);
  };

  const [debouncedQuery, setDebouncedQuery] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 180);
    return () => clearTimeout(t);
  }, [query]);

  // Índice de búsqueda pre-normalizado (se recalcula solo cuando cambia la lista de agentes).
  const indexed = useMemo(
    () => agentes.map((ag) => ({
      ag,
      _s: norm([ag.nombre, ag.email, ag.pais, ag.codigo_ref, ag.codigo_agencia, ag.numero_socio, ag.telefono, ag.plan, ag.observaciones].filter(Boolean).join(' ')),
    })),
    [agentes]
  );

  const filtered = useMemo(() => {
    const q = norm(debouncedQuery.trim());
    const out = [];
    for (const { ag, _s } of indexed) {
      if (estadoFilter !== 'todos' && (ag.estado || 'Activo') !== estadoFilter) continue;
      if (planFilter !== 'todos') {
        if (planFilter === 'sin' ? !!ag.plan : (ag.plan || '') !== planFilter) continue;
      }
      if (soloDeuda && !(ag.deuda_pendiente && parseMonto(ag.deuda_pendiente) > 0)) continue;
      if (soloContratoPendiente && ag.contrato && ag.contrato.has_pdf) continue;
      if (pagoMin && parseMonto(ag.monto_pagado) < parseFloat(pagoMin)) continue;
      if (pagoMax && parseMonto(ag.monto_pagado) > parseFloat(pagoMax)) continue;
      if (genFilter !== 'todas') {
        const g = genOf(ag);
        if (genFilter === 'sin') { if (g !== null) continue; }
        else if (g !== parseInt(genFilter, 10)) continue;
      }
      if (q && !_s.includes(q)) continue;
      out.push(ag);
    }
    return out;
  }, [indexed, debouncedQuery, estadoFilter, planFilter, soloDeuda, soloContratoPendiente, pagoMin, pagoMax, genFilter]);

  const totalDeuda = useMemo(
    () => filtered.reduce((acc, ag) => acc + parseMonto(ag.deuda_pendiente), 0),
    [filtered]
  );
  const conDeudaCount = useMemo(
    () => filtered.filter((ag) => parseMonto(ag.deuda_pendiente) > 0).length,
    [filtered]
  );
  const totalPagado = useMemo(
    () => filtered.reduce((acc, ag) => acc + parseMonto(ag.monto_pagado), 0),
    [filtered]
  );
  const conPagoCount = useMemo(
    () => filtered.filter((ag) => parseMonto(ag.monto_pagado) > 0).length,
    [filtered]
  );

  const [visibleLimit, setVisibleLimit] = useState(60);
  useEffect(() => {
    setVisibleLimit(60);
  }, [debouncedQuery, estadoFilter, planFilter, soloDeuda, soloContratoPendiente, pagoMin, pagoMax, genFilter]);
  const visibles = useMemo(() => filtered.slice(0, visibleLimit), [filtered, visibleLimit]);

  return (
    <>
      <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <UserCheck className="h-5 w-5 text-[#D4AF37]" />
            Crear Nuevo Agente
          </h3>
          <button onClick={importarSocios} disabled={importing}
            className="flex items-center gap-2 px-3 py-2 text-xs font-semibold rounded-lg bg-[#0B1F44] text-white hover:bg-[#13306b] disabled:opacity-60"
            data-testid="agentes-importar-socios">
            <Upload className="h-4 w-4" /> {importing ? 'Importando…' : 'Importar socios (hoja CV)'}
          </button>
          <button onClick={importarCobranza} disabled={importing}
            className="flex items-center gap-2 px-3 py-2 text-xs font-semibold rounded-lg bg-[#8a1c1c] text-white hover:bg-[#a52222] disabled:opacity-60"
            data-testid="agentes-importar-cobranza">
            <Upload className="h-4 w-4" /> {importing ? 'Importando…' : 'Importar cobranza (Mayo 04)'}
          </button>
          <button onClick={importarPagos} disabled={importing}
            className="flex items-center gap-2 px-3 py-2 text-xs font-semibold rounded-lg bg-[#166534] text-white hover:bg-[#15803d] disabled:opacity-60"
            data-testid="agentes-importar-pagos">
            <Upload className="h-4 w-4" /> {importing ? 'Importando…' : 'Importar pagos (hoja CV)'}
          </button>
          <button onClick={fusionarDuplicados} disabled={merging}
            className="flex items-center gap-2 px-3 py-2 text-xs font-semibold rounded-lg bg-[#6d28d9] text-white hover:bg-[#5b21b6] disabled:opacity-60"
            data-testid="agentes-fusionar-duplicados">
            <GitMerge className="h-4 w-4" /> {merging ? 'Fusionando…' : 'Fusionar duplicados'}
          </button>
        </div>
        <p className="text-xs text-slate-500 mb-4">Escribe al menos el <strong>nombre</strong> y pulsa <strong>Crear Agente</strong> (el código de referido se genera solo si lo dejas vacío). Los agentes también se crean automáticamente al generar un contrato, o puedes usar <strong>Importar socios</strong>.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <input type="text" placeholder="Nombre del agente *" value={form.nombre}
            onChange={e => setForm({ ...form, nombre: e.target.value })}
            className="px-3 py-2 text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700"
            data-testid="agente-nombre" />
          <input type="text" placeholder="Código de referido (opcional)" value={form.codigo_ref}
            onChange={e => setForm({ ...form, codigo_ref: e.target.value.toLowerCase().replace(/\s/g, '') })}
            className="px-3 py-2 text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 font-mono"
            data-testid="agente-codigo" />
          <input type="email" placeholder="Email (opcional)" value={form.email}
            onChange={e => setForm({ ...form, email: e.target.value })}
            className="px-3 py-2 text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700"
            data-testid="agente-email" />
          <input type="text" placeholder="Teléfono / WhatsApp (opcional)" value={form.telefono}
            onChange={e => setForm({ ...form, telefono: e.target.value })}
            className="px-3 py-2 text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700"
            data-testid="agente-telefono" />
          <input type="text" placeholder="País (opcional)" value={form.pais}
            onChange={e => setForm({ ...form, pais: e.target.value })}
            className="px-3 py-2 text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700"
            data-testid="agente-pais" />
          <select value={form.plan} onChange={e => setForm({ ...form, plan: e.target.value })}
            className="px-3 py-2 text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700"
            data-testid="agente-plan-nuevo">
            <option value="">Plan (opcional)</option>
            {PLANES.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div className="mt-3">
          <button onClick={handleCreate} disabled={creating}
            className="flex items-center justify-center gap-2 px-5 py-2 bg-[#D4AF37] text-[#1A1A2E] font-semibold rounded-lg hover:bg-[#B8860B] transition-colors text-sm disabled:opacity-60"
            data-testid="agente-crear">
            <Plus className="h-4 w-4" /> {creating ? 'Creando…' : 'Crear Agente'}
          </button>
        </div>
      </div>

      {cobros.length > 0 && (
        <div className="bg-white dark:bg-slate-800/60 rounded-xl p-4 border-2 border-amber-300 dark:border-amber-900/40" data-testid="cobros-proximos-panel">
          <button onClick={() => setShowCobros(v => !v)} className="w-full flex items-center justify-between gap-2" data-testid="cobros-proximos-toggle">
            <h3 className="text-base font-bold flex items-center gap-2 text-[#92400e] dark:text-amber-400">
              <CalendarClock className="h-5 w-5" /> Próximos cobros ({cobros.length})
              <span className="text-xs font-medium text-slate-500">vencen o se acercan en ≤ 3 días</span>
            </h3>
            <span className="text-xs text-slate-400">{showCobros ? 'Ocultar ▲' : 'Ver ▼'}</span>
          </button>
          {showCobros && (
            <ul className="mt-3 space-y-2" data-testid="cobros-proximos-list">
              {cobros.map((c) => (
                <li key={c.cuota_id} className={`flex flex-wrap items-center gap-2 text-xs rounded-md px-3 py-2 border ${c.vencida ? 'bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-900/30' : 'bg-amber-50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-900/30'}`}>
                  {c.vencida ? <AlertTriangle className="h-4 w-4 text-red-600 shrink-0" /> : <CalendarClock className="h-4 w-4 text-amber-600 shrink-0" />}
                  <button onClick={() => { setQuery(c.codigo_ref || c.agente_nombre); setEstadoFilter('todos'); setPlanFilter('todos'); }}
                    className="font-semibold text-[#0B1F44] dark:text-white hover:underline" data-testid={`cobro-ir-${c.cuota_id}`}>
                    {c.agente_nombre}
                  </button>
                  <span className="font-bold text-slate-700 dark:text-slate-200">{fmtMoney(c.monto)}</span>
                  <span className="text-slate-400">·</span>
                  <span className={c.vencida ? 'text-red-600 font-semibold' : 'text-amber-600 font-semibold'}>
                    {fmtFecha(c.fecha)} {c.vencida ? `(vencida hace ${Math.abs(c.dias_restantes)}d)` : (c.dias_restantes === 0 ? '(hoy)' : `(en ${c.dias_restantes}d)`)}
                  </span>
                  {c.descripcion && <span className="text-slate-400">· {c.descripcion}</span>}
                  {c.telefono && (
                    <a href={`https://wa.me/${(c.telefono || '').replace(/[^0-9]/g, '')}?text=${encodeURIComponent(`Hola ${c.agente_nombre.split(' ')[0]}, te recordamos tu pago acordado de ${c.monto} con fecha ${c.fecha}. Cualquier duda con gusto te ayudamos. — Club Viajemos`)}`}
                      target="_blank" rel="noopener noreferrer"
                      className="ml-auto inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-md bg-[#25D366] text-white hover:bg-[#1eb959]"
                      data-testid={`cobro-wa-${c.cuota_id}`}>
                      <MessageCircle className="h-3 w-3" /> Recordar
                    </a>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h3 className="text-lg font-semibold">Agentes Registrados ({filtered.length}/{agentes.length})</h3>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="h-4 w-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input type="text" value={query} onChange={e => setQuery(e.target.value)}
                placeholder="Buscar por nombre, email, país o código…"
                className="pl-8 pr-3 py-2 text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 w-64 max-w-full"
                data-testid="agentes-buscador" />
            </div>
            <select value={estadoFilter} onChange={e => setEstadoFilter(e.target.value)}
              className="px-3 py-2 text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700"
              data-testid="agentes-filtro-estado">
              <option value="todos">Todos</option>
              <option value="Activo">Activo</option>
              <option value="No Activo">No Activo</option>
              <option value="Sin confirmar">Sin confirmar (referidos)</option>
            </select>
            <select value={planFilter} onChange={e => setPlanFilter(e.target.value)}
              className="px-3 py-2 text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700"
              data-testid="agentes-filtro-plan">
              <option value="todos">Todos los planes</option>
              {PLANES.map(p => <option key={p} value={p}>{p}</option>)}
              <option value="sin">Sin plan</option>
            </select>
            <button onClick={() => setSoloDeuda(v => !v)}
              className={`px-3 py-2 text-sm font-semibold rounded-lg border transition-colors ${soloDeuda ? 'bg-[#b42318] text-white border-[#b42318]' : 'bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-200 border-slate-300 dark:border-slate-600 hover:border-[#b42318]'}`}
              data-testid="agentes-filtro-deuda">
              {soloDeuda ? '● Solo con deuda' : 'Solo con deuda'}
            </button>
            <button onClick={() => setSoloContratoPendiente(v => !v)}
              className={`px-3 py-2 text-sm font-semibold rounded-lg border transition-colors ${soloContratoPendiente ? 'bg-[#8a5a00] text-white border-[#8a5a00]' : 'bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-200 border-slate-300 dark:border-slate-600 hover:border-[#8a5a00]'}`}
              data-testid="agentes-filtro-contrato-pendiente">
              {soloContratoPendiente ? '● Contrato pendiente' : 'Contrato pendiente'}
            </button>
            <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700">
              <span className="text-xs font-semibold text-slate-500 whitespace-nowrap">Pagó ≥ $</span>
              <input type="number" min="0" step="1" value={pagoMin} onChange={e => setPagoMin(e.target.value)}
                placeholder="0"
                className="w-20 px-1.5 py-1 text-sm rounded-md border border-slate-200 dark:border-slate-500 bg-transparent"
                data-testid="agentes-filtro-pago-min" />
              {pagoMin && (
                <button onClick={() => setPagoMin('')} className="text-xs text-slate-400 hover:text-slate-600" data-testid="agentes-filtro-pago-clear">✕</button>
              )}
            </div>
            <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700">
              <span className="text-xs font-semibold text-slate-500 whitespace-nowrap">Pagó ≤ $</span>
              <input type="number" min="0" step="1" value={pagoMax} onChange={e => setPagoMax(e.target.value)}
                placeholder="∞"
                className="w-20 px-1.5 py-1 text-sm rounded-md border border-slate-200 dark:border-slate-500 bg-transparent"
                data-testid="agentes-filtro-pago-max" />
              {pagoMax && (
                <button onClick={() => setPagoMax('')} className="text-xs text-slate-400 hover:text-slate-600" data-testid="agentes-filtro-pago-max-clear">✕</button>
              )}
            </div>
            <button onClick={exportarExcel}
              className="flex items-center gap-2 px-3 py-2 text-sm font-semibold rounded-lg bg-[#166534] text-white hover:bg-[#15803d]"
              data-testid="agentes-exportar-excel">
              <Download className="h-4 w-4" /> Exportar Excel
            </button>
            <select value={genFilter} onChange={e => setGenFilter(e.target.value)}
              className="px-3 py-2 text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700"
              data-testid="agentes-filtro-generacion">
              <option value="todas">Todas las generaciones</option>
              {Array.from({ length: 26 }, (_, i) => i + 1).map(g => (
                <option key={g} value={g}>{`Generación ${String(g).padStart(2, '0')}`}</option>
              ))}
              <option value="sin">Sin código (recientes/manuales)</option>
            </select>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3 mb-4 -mt-1" data-testid="agentes-total-cobranza">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#f2fbf4] dark:bg-[#12331f] border border-[#c9ecd4] dark:border-[#245538] text-sm" data-testid="agentes-total-pagado">
            <span className="text-slate-500 dark:text-slate-300">Total pagado:</span>
            <strong className="text-emerald-700 dark:text-emerald-400">{fmtUSD(totalPagado)}</strong>
            <span className="text-xs text-slate-400">({conPagoCount})</span>
          </span>
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#fff6f6] dark:bg-[#3a1e1e] border border-[#f3d4d4] dark:border-[#5a2a2a] text-sm">
            <span className="text-slate-500 dark:text-slate-300">Total cobranza:</span>
            <strong className="text-[#b42318] dark:text-red-400">{fmtUSD(totalDeuda)}</strong>
          </span>
          <span className="text-xs text-slate-500 dark:text-slate-400">{conDeudaCount} agente(s) con deuda {soloDeuda || estadoFilter !== 'todos' || planFilter !== 'todos' || pagoMin || pagoMax || query ? '(en el filtro actual)' : ''}</span>
        </div>
        {agentes.length === 0 ? (
          <p className="text-center text-slate-500 py-8">No hay agentes registrados aun.</p>
        ) : filtered.length === 0 ? (
          <p className="text-center text-slate-500 py-8">Ningún agente coincide con la búsqueda.</p>
        ) : (
          <div className="space-y-3">
            {visibles.map((ag) => (
              <AgenteCard key={ag.id} ag={ag} onPatch={patchAgente} onDelete={deleteAgente} onRefresh={loadAgentes} allAgentes={agentes} onSetPatrocinador={setPatrocinador} />
            ))}
            {filtered.length > visibles.length && (
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-3" data-testid="agentes-ver-mas-bar">
                <span className="text-xs text-slate-500">Mostrando {visibles.length} de {filtered.length}</span>
                <div className="flex items-center gap-2">
                  <button onClick={() => setVisibleLimit(v => v + 60)}
                    className="text-xs font-semibold px-3 py-1.5 rounded-md bg-[#0B1F44] text-white hover:bg-[#13306b]"
                    data-testid="agentes-ver-mas">Ver 60 más</button>
                  <button onClick={() => setVisibleLimit(filtered.length)}
                    className="text-xs font-semibold px-3 py-1.5 rounded-md border border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700"
                    data-testid="agentes-ver-todos">Ver todos ({filtered.length})</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default AgentesTab;
